import { getApiBaseURL } from '../../apiCommon'

const groupPrefix = createApiRoute(ApiModule.INBOUND_DELIVERY, ApiAuthorization.EMPLOYEE)

type TCompleteDeliveryStep = 'started' | 'supply-updating' | 'pz-queued' | 'pz-waiting' | 'done'
type TCompleteDeliveryStatus = 'ok' | 'failed'
interface TCompleteDeliveryHandlers {
	onMessage?: (payload: { status: TCompleteDeliveryStep }) => void
	onProgress?: (payload: { step: Exclude<TCompleteDeliveryStep, 'started' | 'done'> }) => void
	onDone?: (payload: { status: TCompleteDeliveryStatus }) => void
	onError?: (payload: { message?: string }) => void
}

const isBrowserStreamSupported = () => {
	if (typeof window === 'undefined') return false
	return typeof ReadableStream !== 'undefined' && typeof TextDecoder !== 'undefined'
}

const parseSSEEvent = (rawEvent: string) => {
	const lines = rawEvent.split('\n')
	let eventType = 'message'
	const dataLines: string[] = []

	for (const line of lines) {
		if (line.startsWith('event:')) {
			eventType = line.replace('event:', '').trim()
		}
		if (line.startsWith('data:')) {
			dataLines.push(line.replace('data:', '').trim())
		}
	}

	return {
		eventType,
		data: dataLines.join('\n'),
	}
}

const normalizeProgressStep = (step: string): Exclude<TCompleteDeliveryStep, 'started' | 'done'> => {
	switch (step) {
		case 'supply-updated':
		case 'updating':
		case 'supply-updating':
			return 'supply-updating'
		case 'pz-creating':
		case 'pz-queue':
		case 'pz-queued':
			return 'pz-queued'
		case 'pz-created':
		case 'pz-wait':
		case 'pz-waiting':
			return 'pz-waiting'
		default:
			return step as Exclude<TCompleteDeliveryStep, 'started' | 'done'>
	}
}

function handleDoneEvent(parsed: any, handlers: TCompleteDeliveryHandlers): { finished: boolean; success: boolean } {
	if (parsed?.status) handlers.onDone?.({ status: parsed.status })
	return { finished: true, success: parsed?.status === 'ok' }
}

function dispatchSSEEvent(
	eventType: string,
	parsed: any,
	handlers: TCompleteDeliveryHandlers,
): { finished: boolean; success: boolean } | null {
	if (eventType === 'done') return handleDoneEvent(parsed, handlers)
	if (eventType === 'error') {
		handlers.onError?.({ message: parsed?.message })
		return { finished: true, success: false }
	}
	if (eventType === 'message' && parsed?.status === 'started') handlers.onMessage?.({ status: 'started' })
	if (eventType === 'progress' && parsed?.step) handlers.onProgress?.({ step: normalizeProgressStep(parsed.step) })
	return null
}

const processSSEEvent = (
	raw: string,
	handlers: TCompleteDeliveryHandlers,
): { finished: boolean; success: boolean } | null => {
	const { eventType, data } = parseSSEEvent(raw)
	try {
		const parsed = data ? JSON.parse(data) : undefined
		return dispatchSSEEvent(eventType, parsed, handlers)
	} catch (error) {
		console.error('Nie udało się sparsować zdarzenia SSE', error)
	}
	return null
}

const readEventStream = async (
	response: Response,
	handlers: TCompleteDeliveryHandlers,
	controller: AbortController,
) => {
	const reader = response.body?.getReader()

	if (!reader) {
		throw new Error('Przeglądarka nie wspiera odczytu strumieniowego.')
	}

	const decoder = new TextDecoder()
	let buffer = ''
	let success = false
	let finished = false

	while (true) {
		const { value, done } = await reader.read()
		if (done) break

		buffer += decoder.decode(value, { stream: true })
		const events = buffer.split('\n\n')
		buffer = events.pop() ?? ''

		for (const raw of events) {
			const result = processSSEEvent(raw, handlers)
			if (result) {
				finished = result.finished
				success = result.success
			}
		}

		if (finished) break
	}

	controller.abort()
	return success
}

async function streamDeliveryComplete(
	id: number,
	invoice: string,
	handlers: TCompleteDeliveryHandlers,
	baseHeaders: HeadersInit,
): Promise<{ success: boolean; streamed: boolean }> {
	const controller = new AbortController()
	try {
		const baseURL = getApiBaseURL(groupPrefix)
		const streamUrl = new URL(`${groupPrefix}/supplies/${id}/complete`, baseURL)
		streamUrl.searchParams.set('stream', '1')

		const response = await fetch(streamUrl.toString(), {
			method: 'POST',
			headers: baseHeaders,
			credentials: 'include',
			mode: 'cors',
			body: JSON.stringify({ invoice_number: invoice }),
			signal: controller.signal,
		})

		if (response.status === EHttpStatusCodes.HTTP_STATUS_CONFLICT)
			throw new Error('Supply is already being processed.')

		if (response.headers.get('content-type')?.includes('text/event-stream')) {
			const success = await readEventStream(response, handlers, controller)
			return { success, streamed: true }
		}

		const fallbackData = await response.json().catch(() => undefined)
		if (!response.ok) throw new Error(fallbackData?.message || 'Błąd podczas przyjmowania dostawy.')

		handlers.onMessage?.({ status: 'started' })
		handlers.onDone?.({ status: 'ok' })
		return { success: true, streamed: false }
	} catch (error) {
		controller.abort()
		throw error
	}
}

export const apiDelivery = {
	fetchDeliveries: async (
		page: number = 1,
		perPage: number = 25,
	): Promise<IApiResponseModel<IDeliveryDetailsModel[]>> => {
		const { data, meta } = await apiCommon<IDeliveryDetails[]>(`${groupPrefix}/supplies/list`, {
			method: 'GET',
			...createQuery({ page, perPage }),
		})
		return { data: data.map(d => DeliveryDetails(d)), meta }
	},

	fetchHistoryDeliveries: async (
		page: number = 1,
		perPage: number = 25,
	): Promise<IApiResponseModel<IDeliveryDetailsModel[]>> => {
		const { data, meta } = await apiCommon<IDeliveryDetails[]>(`${groupPrefix}/supplies/completed`, {
			method: 'GET',
			...createQuery({ page, perPage }),
		})
		return { data: data.map(d => DeliveryDetails(d)), meta }
	},

	fetchSupplies: async (
		deliveryId: number,
		query: string | undefined,
		page: number = 1,
		perPage: number = 25,
	): Promise<IApiResponseModel<IDeliveryProductModel[]>> => {
		const { data, meta } = await apiCommon<IDeliveryProduct[]>(
			`${groupPrefix}/supplies/${deliveryId}/supply-products`,
			{
				method: 'GET',
				...createQuery({ page, perPage }, undefined, undefined, { query }),
			},
		)
		return { data: data.map(d => DeliveryProduct(d)), meta }
	},

	putSupplies: async (id: number, data: IDeliveryProductModel) => {
		const body = DeliveryProductToJson(data)
		await apiCommon<IDeliveryProduct[]>(`${groupPrefix}/supplies/${id}/supply-products/${data.id}`, {
			method: 'PUT',
			body,
		})
	},

	completeDelivery: async (
		params:
			| {
					id: number
					invoiceNumber: string
					useStream?: boolean
					onMessage?: TCompleteDeliveryHandlers['onMessage']
					onProgress?: TCompleteDeliveryHandlers['onProgress']
					onDone?: TCompleteDeliveryHandlers['onDone']
					onError?: TCompleteDeliveryHandlers['onError']
			  }
			| number,
		invoiceNumber?: string,
	): Promise<{ success: boolean; streamed: boolean }> => {
		const mappedParams =
			typeof params === 'number' ? { id: params, invoiceNumber: invoiceNumber as string } : params
		const { id, invoiceNumber: invoice, useStream = true, onDone, onError, onMessage, onProgress } = mappedParams

		if (!invoice) throw new Error('Brak numeru faktury wymagany do kompletacji dostawy.')

		const xsrfToken = useCookie('XSRF-TOKEN')
		const baseHeaders: HeadersInit = {
			accept: 'text/event-stream',
			'Content-Type': 'application/json',
			'Accept-Language': 'pl',
		}
		if (xsrfToken?.value) baseHeaders['X-XSRF-TOKEN'] = xsrfToken.value

		if (useStream && isBrowserStreamSupported()) {
			return streamDeliveryComplete(id, invoice, { onDone, onError, onMessage, onProgress }, baseHeaders)
		}

		await apiCommon<IDeliveryProduct[]>(`${groupPrefix}/supplies/${id}/complete`, {
			method: 'POST',
			body: { invoice_number: invoice },
		})
		onDone?.({ status: 'ok' })
		return { success: true, streamed: false }
	},

	rejectDelivery: async (id: number, delivery: any) => {
		await apiCommon<IDeliveryProduct[]>(
			`${groupPrefix}/supply/${id}/complaint`,
			{
				method: 'POST',
				body: delivery,
			},
			'pl',
			true,
		)
	},

	reportProblemWithProduct: async (
		supplyProductId: number,
		body: any,
	): Promise<IApiResponseModel<IDeliveryProductModel>> => {
		const { data } = await apiCommon<IDeliveryProduct>(`${groupPrefix}/supply-product/${supplyProductId}/claim`, {
			method: 'POST',
			body,
		})
		return { data: DeliveryProduct(data) }
	},

	importDeliveryDocument: async (supply: number, file: File) => {
		const formData = new FormData()
		formData.append('file', file)
		await apiCommon<any>(
			`${groupPrefix}/supplies/${supply}/document`,
			{
				method: 'POST',
				body: formData,
			},
			'pl',
			true,
		)
	},

	fetchDeliveryDetails: async (deliveryId: number): Promise<IDeliveryDetailsModel> => {
		const { data } = await apiCommon<IDeliveryDetails>(`${groupPrefix}/supplies/${deliveryId}`, {
			method: 'GET',
		})
		return DeliveryDetails(data)
	},

	deleteDeliveryDocument: async (deliveryId: number) => {
		await apiCommon<any>(`${groupPrefix}/supplies/${deliveryId}/document`, {
			method: 'DELETE',
		})
	},

	downloadDeliveryDocument: async (deliveryId: number, fileName: string) => {
		const response = await apiCommon<Blob>(`${groupPrefix}/supplies/${deliveryId}/document`, {
			method: 'GET',
			responseType: 'blob',
		})

		const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
		const url = URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.setAttribute('download', fileName)
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		URL.revokeObjectURL(url)
	},
}
